/* eslint-disable react/no-unstable-nested-components */
import type { ColumnDefinition } from 'components/SubTable';
import {
  useGetExternalPaymentsSuspense,
  useGetInvoicesSuspense,
} from 'api/payments/generated/fragmentPaymentsAPI';
import { MainViewLayout } from 'components/Layout/MainViewLayout';
import { SubTable } from 'components/SubTable';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import React, { Suspense, useMemo, useState } from 'react';
import { makePath } from 'utils/routes';
import { useLocation } from 'wouter';
import { usePlatformEntity } from 'wrappers/PlatformEntityProvider';

import { DetailField } from '@fragment/ui/src/components/DetailField/DetailField';
import { Icon } from '@fragment/ui/src/components/Icon/BaseIcon/Icon';
import { StatusBadge } from '@fragment/ui/src/components/StatusBadge/StatusBadge';
import { TabsV2 } from '@fragment/ui/src/components/Tabs/TabsV2';

import type { InvoiceItem } from './ItemsPane';
import type { Obligation } from './OverviewPane';
import { ItemsPane } from './ItemsPane';
import { OverviewPane } from './OverviewPane';

type Props = {
  workspaceId: string;
  invoiceId: string;
};

type TabValue = 'overview' | 'items' | 'payments' | 'history';

type Payment = {
  id: string;
  party: string;
  status: string;
  paymentMethod: string;
  direction: string;
  amount: number;
  date: string;
};

const formatCurrency = (amount: number) =>
  `$${(amount / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatDate = (dateString: string) =>
  new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

// Define schema for payments table
const paymentsSchema: readonly ColumnDefinition<Payment>[] = [
  {
    title: 'Party',
    format: (payment: Payment) => payment.party,
  },
  {
    title: 'Account',
    format: (payment: Payment) => payment.paymentMethod,
  },
  {
    title: 'Date',
    format: (payment: Payment) => formatDate(payment.date),
  },
  {
    title: 'Amount',
    justify: 'right' as const,
    format: (payment: Payment) => formatCurrency(payment.amount),
  },
];

const InvoiceDetailPageContent = ({
  workspaceId,
  invoiceId,
}: Props): JSX.Element => {
  const { data } = useGetInvoicesSuspense();
  const { data: externalPaymentsData } = useGetExternalPaymentsSuspense();
  const [, setLocation] = useLocation();
  const [activeTab, setActiveTab] = useState<TabValue>('overview');
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [showDispute, setShowDispute] = useState(false);
  const [acceptedDispute, setAcceptedDispute] = useState(false);

  // Find the specific invoice by id
  const invoice = useMemo(
    () => data.data.find((inv) => inv.id === invoiceId),
    [data, invoiceId]
  );

  // Filter and map external payments for this invoice
  const payments: Payment[] = useMemo(
    () =>
      externalPaymentsData.data
        .filter((payment) => payment.invoiceId === invoiceId)
        .map((payment) => ({
          id: payment.transactionId,
          party: payment.counterpartyId,
          status: 'completed',
          paymentMethod: payment.accountReference,
          direction: 'in',
          amount: Number(payment.amount),
          date: payment.created,
        })),
    [externalPaymentsData, invoiceId]
  );

  useDocumentTitle(invoice ? `Invoice ${invoice.ik}` : 'Invoice Detail');

  const { platformEntity } = usePlatformEntity();

  // Original items from invoice (unchanged)
  const originalItems: InvoiceItem[] = useMemo(() => {
    if (!invoice?.lineItems) return [];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return invoice.lineItems.map((item: any) => ({
      id: item.id,
      description: item.description,
      productId: item.product_id,
      payoutParty: item.payout_party.platform
        ? 'Platform'
        : item.payout_party.party_id,
      platform: item.payout_party.platform ?? false,
      amount: item.amount,
    }));
  }, [invoice]);

  // Calculate total paid from external payments for this invoice
  const totalPaidFromExternalPayments = useMemo(
    () =>
      externalPaymentsData.data
        .filter((payment) => payment.invoiceId === invoiceId)
        .reduce((sum, payment) => sum + Number(payment.amount), 0),
    [externalPaymentsData, invoiceId]
  );

  // Extract base obligations (payin and payout) from invoice
  const baseObligations = useMemo(() => {
    if (!invoice) return { payin: [], payout: [] };

    const payin: Obligation[] = [];
    const payoutMap = new Map<string, { amount: number; paid: number }>();

    // Calculate buyer obligation (sum of all line items)
    if (invoice.buyerParty && invoice.lineItems) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const totalAmount = invoice.lineItems.reduce(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (sum: any, item: any) => sum + Number(item.amount),
        0
      );
      payin.push({
        id: `payin-${invoice.buyerParty}`,
        name: invoice.buyerParty,
        type: 'payin' as const,
        amount: totalAmount,
        paid: totalPaidFromExternalPayments,
      });
    }

    // Calculate seller obligations (grouped by payout_party)
    if (invoice.lineItems) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      invoice.lineItems.forEach((item: any) => {
        const amount = Number(item.amount);
        const party = item.payout_party.platform
          ? // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            platformEntity?.displayName ?? 'Platform'
          : item.payout_party.party_id;
        const existing = payoutMap.get(party) || {
          amount: 0,
          paid: 0,
        };
        existing.amount += amount;
        payoutMap.set(party, existing);
      });
    }

    const payout: Obligation[] = Array.from(payoutMap.entries()).map(
      ([name, obligationData]) => ({
        id: `payout-${name}`,
        name,
        type: 'payout' as const,
        amount: obligationData.amount,
        paid: 0, // Payout tracking not yet implemented
      })
    );

    return { payin, payout };
  }, [invoice, platformEntity, totalPaidFromExternalPayments]);

  // Check if invoice is disputed
  const isDisputed = useMemo(
    () => invoice?.ik.toLowerCase().includes('green') || false,
    [invoice]
  );

  // Generate dispute message from a random seller
  const disputeMessage = useMemo(() => {
    if (!isDisputed || !baseObligations.payout.length || !originalItems.length)
      return null;

    // Pick a random seller (payout party)
    const randomSeller =
      baseObligations.payout[
        Math.floor(Math.random() * baseObligations.payout.length)
      ];

    // Pick a random item
    const randomItem =
      originalItems[Math.floor(Math.random() * originalItems.length)];

    // Generate a price that's different from the actual price
    const actualPrice = Number(randomItem.amount);
    const disputedPrice = actualPrice * (1.1 + Math.random() * 1.5); // 70-120% of actual

    return {
      seller: randomSeller.name,
      message: `Hey, the price for "${
        randomItem.description
      }" is ${formatCurrency(actualPrice)}, it should be ${formatCurrency(
        Math.round(disputedPrice)
      )}`,
      itemId: randomItem.id,
      disputedPrice: Math.round(disputedPrice),
    };
  }, [isDisputed, baseObligations.payout, originalItems]);

  // Base items with accepted dispute applied (if accepted)
  const baseItems: InvoiceItem[] = useMemo(() => {
    if (!acceptedDispute || !disputeMessage) return originalItems;

    return originalItems.map((item) => {
      if (item.id === disputeMessage.itemId) {
        return {
          ...item,
          amount: disputeMessage.disputedPrice,
        };
      }
      return item;
    });
  }, [originalItems, acceptedDispute, disputeMessage]);

  // Calculate total from items
  const total = useMemo(
    () =>
      baseItems.reduce(
        (sum: number, item: InvoiceItem) => sum + Number(item.amount),
        0
      ),
    [baseItems]
  );

  // Calculate the dispute delta (price difference)
  const disputeDelta = useMemo(() => {
    if (!disputeMessage) return 0;

    const originalItem = originalItems.find(
      (item) => item.id === disputeMessage.itemId
    );
    if (!originalItem) return 0;

    return disputeMessage.disputedPrice - Number(originalItem.amount);
  }, [disputeMessage, originalItems]);

  // Base obligations with accepted dispute applied (if accepted)
  const adjustedBaseObligations = useMemo(() => {
    if (!acceptedDispute || !disputeMessage || disputeDelta === 0) {
      return baseObligations;
    }

    // Apply dispute delta to all obligations permanently
    const adjustedPayin: Obligation[] = baseObligations.payin.map(
      (obligation) => ({
        ...obligation,
        amount: obligation.amount + disputeDelta,
        paid: obligation.paid + disputeDelta,
      })
    );

    const adjustedPayout: Obligation[] = baseObligations.payout.map(
      (obligation) => ({
        ...obligation,
        amount: obligation.amount + disputeDelta,
      })
    );

    return { payin: adjustedPayin, payout: adjustedPayout };
  }, [baseObligations, acceptedDispute, disputeMessage, disputeDelta]);

  // Obligations to display (modified when showing dispute)
  const obligations = useMemo(() => {
    if (
      !showDispute ||
      !disputeMessage ||
      disputeDelta === 0 ||
      acceptedDispute
    ) {
      return adjustedBaseObligations;
    }

    // Apply dispute delta to all obligations
    const modifiedPayin: Obligation[] = [];
    adjustedBaseObligations.payin.forEach((obligation) => {
      // Add original obligation with strikethrough flag
      modifiedPayin.push({
        ...obligation,
        isStrikethrough: true,
      });
      // Add new obligation with adjusted amount
      modifiedPayin.push({
        ...obligation,
        id: `${obligation.id}-disputed`,
        amount: obligation.amount + disputeDelta,
        paid: obligation.paid + disputeDelta,
        isDisputed: true,
      });
    });

    const modifiedPayout: Obligation[] = [];
    adjustedBaseObligations.payout.forEach((obligation) => {
      // Add original obligation with strikethrough flag
      modifiedPayout.push({
        ...obligation,
        isStrikethrough: true,
      });
      // Add new obligation with adjusted amount
      modifiedPayout.push({
        ...obligation,
        id: `${obligation.id}-disputed`,
        amount: obligation.amount + disputeDelta,
        isDisputed: true,
      });
    });

    return { payin: modifiedPayin, payout: modifiedPayout };
  }, [
    adjustedBaseObligations,
    showDispute,
    disputeMessage,
    disputeDelta,
    acceptedDispute,
  ]);

  // Items to display in table (modified when showing dispute)
  const items: InvoiceItem[] = useMemo(() => {
    if (!showDispute || !disputeMessage || acceptedDispute) return baseItems;

    // Find the disputed item and add strikethrough + new row
    const result: InvoiceItem[] = [];
    baseItems.forEach((item) => {
      if (item.id === disputeMessage.itemId) {
        // Add original item with strikethrough flag
        result.push({
          ...item,
          isStrikethrough: true,
        });
        // Add new item with disputed price
        result.push({
          id: `${item.id}-disputed`,
          description: item.description,
          productId: item.productId,
          payoutParty: item.payoutParty,
          platform: item.platform,
          amount: disputeMessage.disputedPrice,
          isDisputed: true,
        });
      } else {
        result.push(item);
      }
    });
    return result;
  }, [baseItems, showDispute, disputeMessage, acceptedDispute]);

  const handleBackToInvoices = () => {
    setLocation(
      makePath(['w', workspaceId, 'payments', 'invoices'], { absolute: true })
    );
  };

  const handleObligationClick = (obligation: Obligation) => {
    // Navigate to party detail page
    setLocation(
      makePath(['w', workspaceId, 'payments', 'parties', obligation.name], {
        absolute: true,
      })
    );
  };

  const tabOptions = [
    { label: 'Overview', value: 'overview' as const },
    { label: 'Items', value: 'items' as const },
    { label: 'Payments', value: 'payments' as const },
    { label: 'History', value: 'history' as const },
  ];

  const handlePaymentClick = (payment: Payment) => {
    // Future: navigate to payment detail or show payment details
    // eslint-disable-next-line no-console
    console.log('Clicked payment:', payment);
  };

  if (!invoice) {
    return (
      <MainViewLayout hideSearch>
        <div className="text-main-500">Invoice not found</div>
      </MainViewLayout>
    );
  }

  return (
    <MainViewLayout mode="payments" hideSearch>
      <div className="flex w-full h-full relative">
        <div className="flex flex-col w-full h-full">
          <DetailField
            label={
              <div className="flex items-center gap-f2">
                <button
                  type="button"
                  onClick={handleBackToInvoices}
                  className="text-md text-main hover:text-main-500 cursor-pointer"
                >
                  ←
                </button>
              </div>
            }
            padding={false}
          >
            <DetailField
              label={<div className="text-lg">📄</div>}
              padding={false}
              className="flex-1"
            >
              <div className="flex items-center h-full w-fit ml-auto">
                <button
                  aria-label="share"
                  type="button"
                  className="text-md text-main hover:text-main-500 cursor-pointer h-full aspect-square border-l-[0.5px] border-r-[0.5px] border-main-300 flex items-center justify-center"
                  title="Share"
                >
                  <Icon type="external" size="md" />
                </button>
                <button
                  aria-label="download"
                  type="button"
                  className="text-md text-main hover:text-main-500 cursor-pointer h-full aspect-square border-r-[0.5px] border-main-300 flex items-center justify-center"
                  title="Download"
                >
                  <Icon type="download" size="md" />
                </button>
                <button
                  type="button"
                  aria-label="chat"
                  onClick={() => setIsChatOpen(!isChatOpen)}
                  className="text-md text-main hover:text-main-500 cursor-pointer h-full aspect-square border-r-[0.5px] border-main-300 flex items-center justify-center relative"
                  title="Chat"
                >
                  💬
                  {isDisputed && !acceptedDispute && (
                    <span className="absolute -top-1 -right-1 bg-liability-red text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                      1
                    </span>
                  )}
                </button>
                <button
                  type="button"
                  className="text-md text-main hover:text-main-500 cursor-pointer h-full aspect-square flex items-center justify-center"
                  title="Search"
                  aria-label="Search"
                >
                  <Icon type="search" size="md" />
                </button>
              </div>
            </DetailField>
          </DetailField>
          {/* Invoice header with name and status */}
          <DetailField label="Name" className="w-full" padding={false}>
            <div className="flex w-full justify-between h-f8">
              <div className="text-lg p-f1">{invoice.ik}</div>
              <StatusBadge
                className="h-full capitalize border-0 border-l-[0.5px]"
                status={
                  invoice.status as
                    | 'open'
                    | 'closed'
                    | 'draft'
                    | 'active'
                    | 'void'
                    | 'failed'
                }
              />
            </div>
          </DetailField>
          {/* Total field */}
          <DetailField label="Amount">{formatCurrency(total)}</DetailField>

          {/* Date field */}
          <DetailField label="Issued">
            {formatDate(invoice.created)}
          </DetailField>

          <div className="h-f4" />

          {/* Tabs */}
          <TabsV2
            options={tabOptions}
            selectedValue={activeTab}
            onClick={setActiveTab}
          />

          {/* Tab Content */}
          {activeTab === 'overview' && (
            <OverviewPane
              obligations={obligations}
              onObligationClick={handleObligationClick}
            />
          )}

          {activeTab === 'items' && (
            <DetailField
              label="Items"
              padding={false}
              className="border-t-[0.5px]"
            >
              <div className="w-full">
                <ItemsPane
                  items={items}
                  platformDisplayName={
                    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                    platformEntity?.displayName ?? 'Platform'
                  }
                />
              </div>
            </DetailField>
          )}

          {activeTab === 'payments' && (
            <DetailField
              label="Payments"
              padding={false}
              className="border-t-[0.5px]"
            >
              <div className="w-full">
                <SubTable
                  data={payments}
                  schema={paymentsSchema}
                  onClick={handlePaymentClick}
                  textSize="md"
                  showHeader
                  stickyHeader={false}
                  emptyValue={{
                    Party: <span className="text-main-500">No payments</span>,
                    Account: '',
                    Date: '',
                    Amount: '',
                  }}
                />
              </div>
            </DetailField>
          )}

          {activeTab === 'history' && (
            <div className="p-f4 text-main-500 border-t-[0.5px] border-main-300">
              History view coming soon
            </div>
          )}
        </div>

        {/* Chat UI Sidebar */}
        {isChatOpen && isDisputed && disputeMessage && (
          <div className="w-80 border-l-[0.5px] border-main-300 flex flex-col bg-white [box-shadow:none]">
            {/* Chat Header */}
            <div className="flex items-center justify-between p-f2 border-b-[0.5px] border-main-300">
              <div className="flex items-center gap-f1">
                <span className="text-md font-medium">💬 Disputes</span>
                {!acceptedDispute && (
                  <span className="bg-liability-red text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                    1
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={() => setIsChatOpen(false)}
                className="text-main hover:text-main-500 cursor-pointer"
                title="Close chat"
              >
                ✕
              </button>
            </div>

            {/* Chat Messages */}
            <div className="flex-1 p-f2 overflow-y-auto [box-shadow:none]">
              <div className="flex flex-col gap-f2">
                {/* Dispute Message */}
                <div className="flex flex-col gap-f1">
                  <div className="flex items-center gap-f1">
                    <div className="w-6 h-6 rounded-full bg-main-300 flex items-center justify-center text-xs">
                      {disputeMessage.seller.charAt(0).toUpperCase()}
                    </div>
                    <span className="text-sm font-medium">
                      {disputeMessage.seller}
                    </span>
                    <span className="text-xs text-main-500 ml-auto">
                      {new Date().toLocaleTimeString('en-US', {
                        hour: 'numeric',
                        minute: '2-digit',
                      })}
                    </span>
                  </div>
                  <div className="ml-7 flex flex-col gap-f1">
                    <div className="p-f2 bg-main-100 rounded-lg text-sm">
                      {disputeMessage.message}
                    </div>
                    {!acceptedDispute && (
                      <button
                        type="button"
                        onClick={() => setShowDispute(!showDispute)}
                        className="text-xs text-main hover:text-main-500 underline self-start"
                      >
                        {showDispute ? 'Hide' : 'Show'} dispute in items
                      </button>
                    )}
                    {showDispute && !acceptedDispute && (
                      <div className="flex gap-f1 mt-f1">
                        <button
                          type="button"
                          className="px-f2 py-f1 bg-asset-green text-white rounded text-xs hover:opacity-80"
                          onClick={() => {
                            setAcceptedDispute(true);
                            setShowDispute(false);
                          }}
                        >
                          Accept
                        </button>
                        <button
                          type="button"
                          className="px-f2 py-f1 bg-liability-red text-white rounded text-xs hover:opacity-80"
                          onClick={() => {
                            // TODO: Handle decline dispute
                            setShowDispute(false);
                          }}
                        >
                          Decline
                        </button>
                      </div>
                    )}
                    {acceptedDispute && (
                      <div className="text-xs text-asset-green mt-f1">
                        ✓ Dispute accepted
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Chat Input */}
            <div className="p-f2 border-t-[0.5px] border-main-300">
              <div className="flex gap-f1">
                <input
                  type="text"
                  placeholder="Type a message..."
                  className="flex-1 px-f2 py-f1 border-[0.5px] border-main-300 rounded text-sm focus:outline-none focus:border-main-500"
                />
                <button
                  type="button"
                  className="px-f2 py-f1 bg-main text-white rounded text-sm hover:bg-main-500"
                >
                  Send
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainViewLayout>
  );
};

export const InvoiceDetailPage = ({
  workspaceId,
  invoiceId,
}: Props): JSX.Element => (
  <Suspense
    fallback={
      <MainViewLayout hideSearch>
        <div className="text-main-500">Loading invoice...</div>
      </MainViewLayout>
    }
  >
    <InvoiceDetailPageContent workspaceId={workspaceId} invoiceId={invoiceId} />
  </Suspense>
);
